import { memo, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAutocomplete,
  MiButton,
  MiIcon,
  MiPlaceholderArea,
} from "@miplatform/components";
import { ICONS_WEB } from "@miplatform/constants";
import { isEmpty } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useHomeAndAccountSearchViewModel } from "./useHomeAndAccountSearchViewModel";

const TYPES = {
  HOME: "home",
  ACCOUNT: "account",
};

/**
 *
 * @param {Object} Props
 * @param {Object} [Props.account] - The account to preselect in the search.
 * @param {string} [Props.type] - The type of search, either 'home' or 'account'. Defaults to 'home'.
 * @param {Function} [Props.onCreate] - Callback function when creating a new home or account.
 * @param {Function} [Props.onSelect] - Callback function when an item is selected.
 * @param {Function} [Props.onReset] - Callback function when the search is reset.
 * @param {boolean} [Props.showLogo] - Whether to show the logo placeholder.
 * @param {boolean} [Props.disabled] - Whether the search input is disabled.
 * @param {Object} [Props.homeFilters] - Additional home filters for the search.
 * @param {boolean} [Props.hideCreateNewOption] - Whether to hide the "Create New" option in the dropdown.
 * @param {boolean} [Props.required] - Whether the input is required.
 * @returns
 */
export function HomeAndAccountSearch({
  // TODO rename to HOME OR ACCOUNT SEARCH
  account,
  value,
  type = TYPES.HOME,
  required = false,
  filterByAccountTypes = [],
  onCreate = () => null,
  onSelect = () => null,
  onReset = () => null,
  showLogo = false,
  disabled = false,
  hideCreateNewOption = false,
  homeFilters = {},
}) {
  const { classes, theme } = useStyles();
  const [isSelected, setIsSelected] = useState(false);

  const {
    accounts,
    search,
    debouncedSearch,
    homes,
    setSearch,
    getHomes,
    getAccounts,
  } = useHomeAndAccountSearchViewModel({ homeFilters });

  useEffect(() => {
    if (!account) {
      if (type === TYPES.HOME) {
        getHomes();
      }

      if (type === TYPES.ACCOUNT) {
        getAccounts(filterByAccountTypes);
      }
    }
  }, [debouncedSearch, account]);

  useEffect(() => {
    if (account) {
      onSelect(account);
      setSearch(account.name);
      setIsSelected(true);
    }
  }, [account]);

  const handleSelect = (_, value) => {
    if (isEmpty(value)) {
      onCreate();
      return;
    }
    onSelect(value);
    // account || home
    setSearch(value.name || value?.address?.addressLine1 || "");
    setIsSelected(true);
  };

  const handleClear = () => {
    if (debouncedSearch || search) {
      setSearch("");
      onReset();
      setIsSelected(false);
    }
  };

  return (
    <Box data-testid="address-search" className={classes.root}>
      {showLogo && (
        <MiPlaceholderArea
          showLogo
          helperText="Select a Property to get started"
          containerStyle={{
            backgroundColor: theme.palette.white.main,
            height: "auto",
          }}
        />
      )}
      <MiAutocomplete
        disableClearable
        blurOnSelect
        openOnFocus
        clearOnBlur={isSelected}
        disabled={disabled}
        options={type === TYPES.HOME ? homes : accounts}
        filterOptions={(options) => options}
        getOptionLabel={(option) =>
          (type === TYPES.HOME ? option.address?.addressLine1 : option.name) ||
          ""
        }
        inputValue={search || value?.name || value?.address?.addressLine1 || ""}
        onInputChange={(_, value, reason) => {
          if (reason === "input") {
            setSearch(value.trimStart());
            onReset();
            setIsSelected(false);
          } else if (reason === "reset" && !isSelected) {
            setSearch("");
            onReset();
          }
        }}
        onChange={handleSelect}
        renderOption={(props, option) => (
          <MenuItem {...props} key={option.id} className={classes.menuItem}>
            <Typography className={classes.address}>
              {type === TYPES.HOME ? option.address?.addressLine1 : option.name}
            </Typography>
            {type === TYPES.ACCOUNT && (
              <Typography>{option.accountType}</Typography>
            )}
            {type === TYPES.HOME && (
              <>
                <Typography>{option.accountType}</Typography>
                <Typography className={classes.borderLeft}>
                  {option.accountName}
                </Typography>
                <Typography
                  className={option.builder ? classes.borderLeft : ""}
                >
                  {option.builder}
                </Typography>
                <Typography
                  className={option.community ? classes.borderLeft : ""}
                >
                  {option.community}
                </Typography>
                <Box className={classes.badgeGroup} />
              </>
            )}
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={type === TYPES.HOME ? "Search Address" : "Search Customer"}
            fullWidth
            size="small"
            required={TYPES.ACCOUNT === type || required}
            slotProps={{
              input: {
                ...params.InputProps,
                style: {
                  paddingRight: theme.spacing(1.5),
                  fontWeight: search
                    ? theme.fontWeight.semiBold
                    : theme.fontWeight.regular,
                  backgroundColor: theme.palette.common.white,
                },
                inputProps: {
                  "data-testid": `search-input-${type}`,
                  ...params.inputProps,
                },
                startAdornment: (
                  <InputAdornment position="end">
                    <MiIcon
                      path={
                        mdi[isSelected ? ICONS_WEB.ACCOUNT : ICONS_WEB.MAGNIFY]
                      }
                    />
                  </InputAdornment>
                ),
                endAdornment: !disabled && search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClear}>
                      <MiIcon path={mdi[ICONS_WEB.CLOSE]} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
        {...(!hideCreateNewOption && {
          slotProps: {
            paper: {
              type,
              onMouseDown: onCreate,
            },
          },
          PaperComponent: AutocompletePaper,
        })}
      />
    </Box>
  );
}

const AutocompletePaper = memo(({ type, children, onMouseDown, ...props }) => {
  const { classes, theme } = useStyles();

  return (
    <Paper {...props}>
      <MenuItem
        className={classes.menuItem}
        style={{ marginBottom: theme.spacing(-3) }}
      >
        <MiButton
          fullWidth
          title={`Create New ${type === TYPES.HOME ? "Home" : "Customer"}`}
          startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
          sx={{
            padding: theme.spacing(0.5, 0),
            justifyContent: "flex-start",
            color: "blue.main",
          }}
          onMouseDown={onMouseDown}
        />
      </MenuItem>
      {children}
    </Paper>
  );
});

const centerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 3,
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    height: "100%",
    gap: theme.spacing(2),
  },
  badgeGroup: { display: "flex", position: "absolute", right: 12, gap: 4 },
  borderLeft: {
    lineHeight: 1,
    paddingLeft: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.bluegrey.main}`,
  },
  createNewOption: {
    ...centerStyle,
  },
  menuItem: {
    ...centerStyle,
    gap: theme.spacing(2),
    whiteSpace: "nowrap",
    overflow: "hidden",
    width: "100%",
    color: theme.palette.bluegrey.main,
  },
  address: {
    fontWeight: theme.fontWeight.bold,
    lineHeight: 2,
    color: theme.palette.common.black,
  },
}));
